import { useState } from "react"
import { Plus, Trash2, Lock } from "lucide-react"
import { Button } from "../../ui"
import { useUpdateProposalRequirementsMutation } from "shared/redux/endpoints/procurementPurchases"
import { showToastSuccess, showToastError } from "utilities/toastUtil"
import { getApiErrorMessage } from "../../orderSubmitFeedback"
import { COMPARISON_SCOPE_TYPE } from "./proposalScoring"

const REQUIREMENT_MAX = 200

export const LOCKED_REASON = "Đã có nhà cung cấp phản hồi báo giá — không thể thay đổi các yêu cầu / điều khoản."

export const cleanTerms = (rows) => rows.map((r) => r.trim()).filter(Boolean)

// One input per requirement (a requirement = one `terms` line, ≤ 200 chars).
export function RequirementInputs({ value, onChange, readOnly = false }) {
  const update = (i, v) => onChange(value.map((r, j) => (j === i ? v : r)))
  const remove = (i) => onChange(value.filter((_, j) => j !== i))
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {value.map((r, i) => (
        <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            className="pc-control"
            style={{ flex: 1 }}
            placeholder={`VD: "Bảo hành tại chỗ 24 tháng"`}
            value={r}
            maxLength={REQUIREMENT_MAX}
            readOnly={readOnly}
            onChange={(e) => update(i, e.target.value)}
          />
          {value.length > 1 && !readOnly && (
            <button
              type="button"
              onClick={() => remove(i)}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--pc-danger)", padding: 4 }}
              aria-label="Xóa"
            >
              <Trash2 size={15} />
            </button>
          )}
        </div>
      ))}
      {!readOnly && (
        <Button
          variant="ghost"
          size="sm"
          leftIcon={<Plus size={14} />}
          onClick={() => onChange([...value, ""])}
          style={{ alignSelf: "flex-start" }}
        >
          Thêm yêu cầu
        </Button>
      )}
    </div>
  )
}

// The OR/PO's requirement set, shared by every vendor: each one is answered on
// the public link and becomes a scoring row in the compare modal. Editable
// here (PUT /proposal-requirements, no RFQ needed) until a vendor responds.
export default function RequirementsSection({ sourceType, sourceId, terms, locked }) {
  const [draft, setDraft] = useState(null) // null = not editing
  const [updateRequirements, { isLoading: saving }] = useUpdateProposalRequirementsMutation()
  const canSave = !!draft && cleanTerms(draft).length > 0 && !saving

  const handleSave = async () => {
    if (!canSave) return
    try {
      await updateRequirements({
        scopeType: COMPARISON_SCOPE_TYPE[sourceType],
        scopeId: sourceId,
        terms: cleanTerms(draft),
      }).unwrap()
      showToastSuccess("Đã cập nhật yêu cầu / điều khoản.")
      setDraft(null)
    } catch (e) {
      showToastError(getApiErrorMessage(e, "Không thể cập nhật yêu cầu / điều khoản. Vui lòng thử lại."))
      // Locked meanwhile — drop the edit, the refetch shows the saved set.
      if (e?.status === 409) setDraft(null)
    }
  }

  return (
    <div
      style={{
        border: "1px solid var(--pc-border)",
        borderRadius: "var(--pc-r-md)",
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Yêu cầu / điều khoản</div>
          <div style={{ fontSize: 13, color: "var(--pc-body)" }}>
            Áp dụng cho mọi nhà cung cấp — họ trả lời từng yêu cầu, bạn chấm điểm khi so sánh báo giá.
          </div>
        </div>
        {!locked && !draft && (
          <Button variant="secondary" size="sm" onClick={() => setDraft(terms.length ? terms : [""])}>
            Sửa
          </Button>
        )}
      </div>

      {locked && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--pc-muted)" }}>
          <Lock size={13} aria-hidden />
          {LOCKED_REASON}
        </div>
      )}

      {draft ? (
        <>
          <RequirementInputs value={draft} onChange={setDraft} />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button variant="secondary" size="sm" onClick={() => setDraft(null)} disabled={saving}>
              Hủy
            </Button>
            <Button variant="primary" size="sm" onClick={handleSave} disabled={!canSave}>
              {saving ? "Đang lưu…" : "Lưu"}
            </Button>
          </div>
        </>
      ) : terms.length > 0 ? (
        <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13.5, color: "var(--pc-body)" }}>
          {terms.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ol>
      ) : (
        <div style={{ fontSize: 13, color: "var(--pc-muted)" }}>Chưa có yêu cầu nào.</div>
      )}
    </div>
  )
}
